import {
  InstantProducts as HeadlessInstantProducts,
  SearchBox as HeadlessSearchBox,
  buildInstantProducts,
  buildStandaloneSearchBox,
} from '@coveo/headless/commerce';
import { Footer, FooterSeoFrameProps, Icon } from '@greystar/designsystem';
import {
  getDictionaryEntry,
  getEngine,
  SEARCHBOX_SUBTITLE_FALLBACK,
  setFocus,
  useAnalytics,
} from '@greystar/lib';
import { SearchBoxCsr, SearchBoxSsr } from '@greystar/ui';
import {
  Text as JssText,
  Link,
  Placeholder,
  useSitecoreContext,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { useI18n } from 'next-localization';
import { useEffect, useState, JSX } from 'react';
import {
  ResultsFieldLink,
  SeoFooterLinkListProps,
  SiteFooterProps,
} from '../../../component-props';
import { BrightEdgeProps } from '@greystar/lib/src/seo/bright-edge-fetcher';

const SiteFooter = (props: SiteFooterProps): JSX.Element => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const analytics = useAnalytics();
  const handleModalClick = () => {
    const eventData = {
      event: 'property_search_open',
      action_type: 'search model open',
      module_name: 'footer',
      inpage_location: 'top',
      click_text: 'Search for homes',
      click_type: 'internal',
      destination_url: '',
    };

    analytics.trackEvent('property_search_open', eventData);
    setIsModalOpen((prev) => !prev);
  };

  useEffect(() => {
    if (isModalOpen) {
      setFocus('modal');
    }
  }, [isModalOpen]);

  const controllerSearchBox: HeadlessSearchBox = buildStandaloneSearchBox(getEngine(), {
    options: {
      redirectionUrl: '/s',
      id: 'searchbox',
    },
  });

  const controllerInstantProducts: HeadlessInstantProducts = buildInstantProducts(getEngine(), {
    options: {
      searchBoxId: 'searchbox',
    },
  });

  const { sitecoreContext } = useSitecoreContext();
  const { t } = useI18n();
  const siteName = sitecoreContext.site?.name ?? '';
  const columns: FooterSeoFrameProps[] = [];
  const brightEdgeDataFromContext = (sitecoreContext as Record<string, unknown>)
    ?.brightEdge as BrightEdgeProps;

  const brightEdgeBodyContent = brightEdgeDataFromContext?.nodes?.find(
    (node) => node?.type === 'bodystr'
  )?.content;

  let parsedBrightEdgeData = null;
  try {
    parsedBrightEdgeData = brightEdgeBodyContent?.trim() ? JSON.parse(brightEdgeBodyContent) : null;
  } catch (error) {
    console.error('Failed to parse BrightEdge data:', error);
    parsedBrightEdgeData = null;
  }
  // Transform BrightEdge data to match design system BrightEdgeProps format
  const brightEdgeProps = parsedBrightEdgeData?.links
    ? {
        links: parsedBrightEdgeData.links.map((link: { h1: string; url: string }) => ({
          text: link.h1,
          url: link.url,
        })),
      }
    : null;

  const trackFooterLinkClick = (
    contentGroup: string,
    clickText: string,
    destinationUrl: string
  ) => {
    const eventData = {
      event: 'footer_interaction',
      action_type: 'link click - footer',
      module_name: 'footer',
      content_group: contentGroup,
      inpage_location: 'bottom',
      click_text: clickText,
      click_type: 'internal',
      destination_url: destinationUrl,
    };

    analytics.trackEvent('footer_interaction', eventData);
  };

  const trackSocialLinkClick = (platformName: string, destinationUrl: string) => {
    const eventData = {
      event: 'outbound_social_link_click',
      action_type: 'social link click',
      module_name: 'footer',
      inpage_location: 'bottom',
      click_text: platformName,
      click_type: 'external',
      destination_url: destinationUrl,
    };

    analytics.trackEvent('outbound_social_link_click', eventData);
  };

  if (props.rendering.placeholders) {
    Object.keys(props.rendering.placeholders).forEach((key) => {
      const placeholder = props.rendering.placeholders ? props.rendering.placeholders[key] : null;
      if (placeholder) {
        const linkList = placeholder[0] as unknown as SeoFooterLinkListProps;
        if (linkList?.fields?.data?.datasource) {
          const titleLinkValue = linkList.fields.data.datasource.field.Link?.value;
          const titleHref = titleLinkValue?.href ?? '#';
          const titleText = titleLinkValue?.text ?? 'Untitled';
          const columnTitle = titleText;

          const title = linkList.fields.data.datasource.field.Link ? (
            <div
              onClick={(e) => {
                e.preventDefault();
                trackFooterLinkClick(columnTitle, titleText, titleHref);
                if (titleHref !== '#') {
                  window.open(titleHref, '_blank');
                }
              }}
              style={{ display: 'contents' }}
            >
              <Link field={linkList.fields.data.datasource.field.Link} />
            </div>
          ) : (
            <></>
          );

          const regularLinks: JSX.Element[] = [];
          const oneTrustLinks: JSX.Element[] = [];

          linkList.fields.data.datasource.children.results.forEach((element: ResultsFieldLink) => {
            const linkValue = element.field.link.value;
            const href = linkValue.href ?? '#';
            const linkText = linkValue.text ?? 'Untitled';
            const linkClass = linkValue.class ?? '';
            const isOneTrustLink = linkClass === 'ot-sdk-show-settings';

            if (isOneTrustLink) {
              oneTrustLinks.push(
                <div key={element.field.link.value.href} style={{ display: 'contents' }}>
                  <Link field={element.field.link} />
                </div>
              );
            } else {
              regularLinks.push(
                <div
                  key={element.field.link.value.href}
                  onClick={(e) => {
                    e.preventDefault();
                    trackFooterLinkClick(columnTitle, linkText, href);
                    if (href !== '#') {
                      window.open(href, '_blank');
                    }
                  }}
                  style={{ display: 'contents' }}
                >
                  <Link field={element.field.link} />
                </div>
              );
            }
          });

          // Combine regular links and OneTrust links
          const links = [...regularLinks, ...oneTrustLinks];

          columns.push({
            title,
            links,
            hastextunderline: true,
          });
        }
      }
    });
  }

  const socialIcons = props.fields.socialicons
    ? props.fields.socialicons.map((icon) => {
        const href = icon.fields.Link?.value?.href ?? '#';
        const platformName = icon.displayName ?? 'Unknown Platform';

        return {
          iconName: icon.name as keyof typeof Icon,
          linkField: {
            value: {
              href,
              text: icon.fields.Title?.value,
            },
          },
          ariaLabel: `Visit ${platformName}`,
          onClick: () => {
            trackSocialLinkClick(platformName, href);
            if (!sitecoreContext.pageEditing) {
              window.open(href, '_blank');
            }
            return false;
          },
        };
      })
    : [];

  return (
    <section>
      {sitecoreContext.pageEditing && (
        <div className="footer">
          <div className="footer--top">
            <div className="footer--linkssection">
              <div className="footer-seo-frame">
                {' '}
                <Placeholder
                  name={`site-footer-col-a-${props.params.DynamicPlaceholderId}`}
                  rendering={props.rendering}
                />
              </div>
            </div>
            <div className="footer--top--column">
              <div className="footer--linkssection">
                <div className="footer-seo-frame">
                  <div className="footer-seo-frame--title hasUnderline">{columns[0].title}</div>
                  <Placeholder
                    name={`site-footer-col-b-${props.params.DynamicPlaceholderId}`}
                    rendering={props.rendering}
                    renderEach={(component, index) => {
                      return (
                        <div className="footer-seo-frame--link" key={index}>
                          {component}
                        </div>
                      );
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="footer--top--column">
              <div className="footer--linkssection">
                <div className="footer-seo-frame">
                  <div className="footer-seo-frame--title hasUnderline">{columns[1].title}</div>
                  <Placeholder
                    name={`site-footer-col-c-${props.params.DynamicPlaceholderId}`}
                    rendering={props.rendering}
                    renderEach={(component, index) => {
                      return (
                        <div className="footer-seo-frame--link" key={index}>
                          {component}
                        </div>
                      );
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="footer--top--column">
              <div className="footer--linkssection">
                <div className="footer-seo-frame">
                  <div className="footer-seo-frame--title hasUnderline">{columns[2].title}</div>
                  <Placeholder
                    name={`site-footer-col-d-${props.params.DynamicPlaceholderId}`}
                    rendering={props.rendering}
                    renderEach={(component, index) => {
                      return (
                        <div className="footer-seo-frame--link" key={index}>
                          {component}
                        </div>
                      );
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <Footer
        brightedge={brightEdgeProps}
        equalHousingLabel={getDictionaryEntry(
          t('equal-housing-label'),
          'Equal Housing Opportunity'
        )}
        equalHousingHref={props.fields?.footerHousingLink?.value.href ?? ''}
        {...(!sitecoreContext.pageEditing ? { footerFrame: columns } : { footerFrame: [] })}
        onClick={handleModalClick}
        miniButtonLabel="Search for homes"
        icon={{
          position: 'right',
          name: 'Search',
        }}
        iconlistProps={{
          icons: socialIcons,
        }}
        copyrighttext={<JssText field={props.fields.copyright} />}
        disclaimertext={<JssText field={props.fields.disclaimer} />}
      />
      {isModalOpen &&
        (() => {
          if (siteName === 'PDP' || siteName === 'Editorial') {
            return (
              <SearchBoxCsr
                variant="modal"
                controllerSearchBox={controllerSearchBox}
                controllerInstantProducts={controllerInstantProducts}
                headerTitle={getDictionaryEntry(t('searchbox_title'), 'Search for homes')}
                headerSubtitle={getDictionaryEntry(
                  t('searchbox_subtitle'),
                  SEARCHBOX_SUBTITLE_FALLBACK
                )}
                onClose={handleModalClick}
                searchPromptText={getDictionaryEntry(t('searchbox_prompttext'), 'Search for homes')}
                searchResultsTitle=""
              />
            );
          }

          if (siteName === 'Search') {
            return (
              <SearchBoxSsr
                variant="modal"
                headerTitle={getDictionaryEntry(t('searchbox_title'), 'Search for homes')}
                headerSubtitle={getDictionaryEntry(
                  t('searchbox_subtitle'),
                  SEARCHBOX_SUBTITLE_FALLBACK
                )}
                onClose={handleModalClick}
                searchPromptText={getDictionaryEntry(t('searchbox_prompttext'), 'Search for homes')}
                searchResultsTitle=""
              />
            );
          }

          return null;
        })()}
    </section>
  );
};

export default withDatasourceCheck()<SiteFooterProps>(SiteFooter);
